import React, { useEffect, useState } from 'react'
import { phpIniAPI } from '@hpanel/web-common/api/client'

/**
 * Per-domain (= per-container) php.ini editor for tenants.
 *
 * Loads the tenant's body and the operator's global defaults; tenant
 * sees both — global is read-only context, tenant is the editable
 * surface. Save calls PUT /api/php-ini/domains/{id}; the API returns
 * the persisted view and a restart fires in the background. Validation
 * errors come back as a 422 with the offending line number in `detail`.
 *
 * Per CLAUDE.md §9, the API client wraps fetch and the surfaced error
 * message is the server's `detail` verbatim — we do NOT replace it
 * with a generic "save failed".
 */
export interface PhpIniEditorProps {
  domainId: number
}

export const PhpIniEditor: React.FC<PhpIniEditorProps> = ({ domainId }) => {
  const [tenantBody, setTenantBody] = useState<string>('')
  const [globalBody, setGlobalBody] = useState<string>('')
  const [draft, setDraft] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(true)
  const [saving, setSaving] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [savedAt, setSavedAt] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    phpIniAPI
      .getTenant(domainId)
      .then((resp) => {
        if (cancelled) return
        setTenantBody(resp.tenant ?? '')
        setGlobalBody(resp.global ?? '')
        setDraft(resp.tenant ?? '')
      })
      .catch((e: Error) => {
        if (cancelled) return
        console.error('phpIniAPI.getTenant failed', e)
        setError(e.message || 'Failed to load PHP settings')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [domainId])

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    try {
      const resp = await phpIniAPI.updateTenant(domainId, draft)
      setTenantBody(resp.tenant ?? '')
      setDraft(resp.tenant ?? '')
      setSavedAt(new Date().toLocaleTimeString())
    } catch (e) {
      console.error('phpIniAPI.updateTenant failed', e)
      setError(
        e instanceof Error ? e.message : 'Failed to save PHP settings',
      )
    } finally {
      setSaving(false)
    }
  }

  const handleReset = () => {
    setDraft(tenantBody)
    setError(null)
  }

  const isDirty = draft !== tenantBody

  return (
    <div data-testid="php-ini-editor" className="space-y-4">
      <div>
        <h3 className="text-base font-semibold text-gray-800 dark:text-gray-200">
          PHP settings
        </h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          Per-container php.ini drop-in. The operator's global defaults
          are loaded first; whatever you put here overrides them on a
          per-key basis. After saving, your container restarts so PHP
          picks up the change.
        </p>
      </div>

      <div>
        <label
          htmlFor="php-ini-global"
          className="text-xs font-medium text-gray-600 dark:text-gray-400"
        >
          Global defaults (read-only)
        </label>
        <textarea
          id="php-ini-global"
          data-testid="php-ini-editor-global"
          value={globalBody}
          readOnly
          rows={6}
          className="input font-mono text-xs bg-gray-50 dark:bg-gray-900 mt-1"
        />
      </div>

      <div>
        <label
          htmlFor="php-ini-tenant"
          className="text-xs font-medium text-gray-600 dark:text-gray-400"
        >
          Your overrides
        </label>
        <textarea
          id="php-ini-tenant"
          data-testid="php-ini-editor-tenant"
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value)
            setError(null)
          }}
          rows={10}
          spellCheck={false}
          placeholder={'memory_limit = 1G\nupload_max_filesize = 128M'}
          disabled={loading || saving}
          className="input font-mono text-xs mt-1"
        />
      </div>

      {error && (
        <div
          data-testid="php-ini-editor-error"
          role="alert"
          className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3"
        >
          <p className="text-red-600 dark:text-red-400 text-sm">⚠️ {error}</p>
        </div>
      )}

      {savedAt && !isDirty && !error && (
        <p
          data-testid="php-ini-editor-saved"
          className="text-xs text-green-600 dark:text-green-400"
        >
          Saved at {savedAt}. Container restart fired.
        </p>
      )}

      <div className="flex gap-2">
        <button
          type="button"
          data-testid="php-ini-editor-save"
          onClick={handleSave}
          disabled={!isDirty || saving || loading}
          className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button
          type="button"
          data-testid="php-ini-editor-reset"
          onClick={handleReset}
          disabled={!isDirty || saving}
          className="btn-secondary"
        >
          Reset
        </button>
      </div>
    </div>
  )
}
